import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import purchasesService from '../services/purchasesService';
import productService from '../services/productService';
import measureService from '../services/measureService';
import { useNotification } from '../context/NotificationContext';
import AuthContext from '../context/AuthContext';
import { todayInTimezone } from '../utils/datetime';
import SupplierSearch from './SupplierSearch';
import PurchaseOrderItemRow from './PurchaseOrderItemRow';
import RegisterPaymentModal from './RegisterPaymentModal';
import { normalizeNumericInput, toNumber } from '../utils/numericInput';

const FREQUENCIES = [
  'mensual', 'bimestral', 'semestral', 'anual', 'recurrente', 'ocasional', 'unica'
];

const emptyItem = () => ({
  productId: null,
  productName: '',
  measure: '',
  description: '',
  quantity: '1',
  unit_price: '0',
  is_inventoried: false
});

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);

const toItemState = (item) => ({
  productId: item.product ?? null,
  productName: item.product_name || '',
  measure: item.measure ?? '',
  description: item.description || '',
  quantity: normalizeNumericInput(item.quantity),
  unit_price: normalizeNumericInput(item.unit_price),
  is_inventoried: !!item.is_inventoried
});

/**
 * PurchaseOrderForm
 * Shared create/edit form. Totals are derived from the line array on every
 * render - they are never stored in state, so they cannot drift from the grid.
 * The server revalidates the same arithmetic and rejects a mismatch with a 400.
 */
const PurchaseOrderForm = ({ initialData = null, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { timezone } = useContext(AuthContext);
  const isEditMode = !!initialData?.id;

  const [supplier, setSupplier] = useState(
    initialData?.supplier
      ? { id: initialData.supplier, name: initialData.supplier_name || '' }
      : null
  );
  const [formData, setFormData] = useState({
    supplier_order_number: initialData?.supplier_order_number || '',
    invoice_reference: initialData?.invoice_reference || '',
    operation_type: initialData?.operation_type || '',
    category: initialData?.category || '',
    subcategory: initialData?.subcategory || '',
    effective_date: initialData?.effective_date || todayInTimezone(timezone),
    payment_method: initialData?.payment_method || '',
    frequency: initialData?.frequency || 'unica',
    is_invoiced: initialData?.is_invoiced ?? false,
    is_credit: initialData?.is_credit ?? false,
    taxes: normalizeNumericInput(initialData?.taxes ?? 0),
    taxes2: normalizeNumericInput(initialData?.taxes2 ?? 0)
  });
  // Not part of formData: is_paid is read-only on the serializer and only
  // the payment actions move it. The form shows it and offers the door.
  const [isPaid, setIsPaid] = useState(initialData?.is_paid ?? false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  const [items, setItems] = useState(
    initialData?.items?.length ? initialData.items.map(toItemState) : [emptyItem()]
  );

  const [operationTypes, setOperationTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [products, setProducts] = useState([]);
  const [measures, setMeasures] = useState([]);

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const [operationRes, categoryRes, paymentRes, productRes, measureRes] = await Promise.all([
          purchasesService.listPurchaseTypes({ type: 'OPERATION_TYPE' }),
          purchasesService.listPurchaseTypes({ type: 'CATEGORY' }),
          purchasesService.listPaymentMethods(),
          productService.listProducts(),
          measureService.listMeasures()
        ]);
        setOperationTypes(operationRes.data || []);
        setCategories(categoryRes.data || []);
        setPaymentMethods(paymentRes.data || []);
        setProducts(productRes.data?.results || []);
        setMeasures(measureRes.data?.results || []);
      } catch (err) {
        console.error('Error loading purchase catalogs:', err);
        notify.error(t('common.failedToLoad'));
      }
    };
    loadCatalogs();
  }, [notify, t]);

  // Cascade: refetch subcategories whenever the selected category changes.
  useEffect(() => {
    if (!formData.category) {
      setSubcategories([]);
      return;
    }
    const loadSubcategories = async () => {
      try {
        const res = await purchasesService.listPurchaseTypes({
          type: 'SUBCATEGORY',
          parentId: formData.category
        });
        setSubcategories(res.data || []);
      } catch (err) {
        console.error('Error loading subcategories:', err);
      }
    };
    loadSubcategories();
  }, [formData.category]);

  const subtotal = useMemo(
    () => items.reduce(
      (sum, item) => sum + toNumber(item.quantity) * toNumber(item.unit_price),
      0
    ),
    [items]
  );
  const taxes = toNumber(formData.taxes);
  const taxes2 = toNumber(formData.taxes2);
  const grandTotal = subtotal + taxes + taxes2;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  // Stores the raw (normalised) string so the field can be cleared and never
  // accumulates a stale leading zero.
  const handleNumericChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: normalizeNumericInput(value) }));
  };

  // Changing the category invalidates whichever subcategory was chosen under
  // the old one, so it is cleared in the same update.
  const handleCategoryChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, category: value, subcategory: '' }));
  };

  const handleItemChange = (index, field, value) => {
    setItems((prev) => prev.map(
      (item, i) => (i === index ? { ...item, [field]: value } : item)
    ));
  };

  const handleItemRemove = (index) => {
    setItems((prev) => prev.filter((_item, i) => i !== index));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!supplier?.id) newErrors.supplier = t('purchases.supplierRequired');
    if (!formData.operation_type) newErrors.operation_type = t('purchases.operationTypeRequired');
    if (!formData.category) newErrors.category = t('purchases.categoryRequired');
    if (!formData.subcategory) newErrors.subcategory = t('purchases.subcategoryRequired');
    if (!formData.payment_method) newErrors.payment_method = t('purchases.paymentMethodRequired');
    if (formData.is_invoiced && !formData.invoice_reference.trim()) {
      newErrors.invoice_reference = t('purchases.invoiceReferenceRequired');
    }

    if (items.length === 0) {
      newErrors.items = t('purchases.itemsRequired');
    } else if (items.some((item) => !item.description || !item.description.trim())) {
      newErrors.items = t('purchases.itemDescriptionRequired');
    } else if (items.some((item) => toNumber(item.quantity) <= 0)) {
      newErrors.items = t('purchases.itemQuantityInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = () => ({
    supplier: supplier.id,
    supplier_order_number: formData.supplier_order_number.trim() || null,
    invoice_reference: formData.invoice_reference.trim() || null,
    operation_type: formData.operation_type,
    category: formData.category,
    subcategory: formData.subcategory,
    effective_date: formData.effective_date,
    payment_method: formData.payment_method,
    frequency: formData.frequency,
    is_invoiced: formData.is_invoiced,
    is_credit: formData.is_credit,
    subtotal: subtotal.toFixed(2),
    taxes: taxes.toFixed(2),
    taxes2: taxes2.toFixed(2),
    total: grandTotal.toFixed(2),
    items: items.map((item) => ({
      product: item.productId || null,
      measure: item.measure || null,
      description: item.description.trim(),
      quantity: toNumber(item.quantity),
      unit_price: toNumber(item.unit_price),
      is_inventoried: !!item.is_inventoried
    }))
  });

  const handleMarkUnpaid = async () => {
    try {
      await purchasesService.markUnpaid(initialData.id);
      setIsPaid(false);
      notify.success(t('purchases.markedUnpaid'));
    } catch (err) {
      console.error('Error marking the order unpaid:', err);
      notify.error(t('purchases.failedToMarkUnpaid'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = buildPayload();
      let order;
      if (isEditMode) {
        const res = await purchasesService.updatePurchaseOrder(initialData.id, payload);
        order = res.data;
        notify.success(t('purchases.orderUpdated'));
      } else {
        const res = await purchasesService.createPurchaseOrder(payload);
        order = res.data;
        notify.success(t('purchases.orderCreated'));
      }
      onSuccess(order);
    } catch (err) {
      const errMsg = err.response?.data?.detail
        || (isEditMode ? t('common.failedToUpdate') : t('common.failedToCreate'));
      notify.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <form onSubmit={handleSubmit}>
      <fieldset className="form-fieldset">
        <legend>{t('purchases.generalData')}</legend>

        <div className="grid-3">
          <div className="form-group">
            <label className="form-label required">{t('table.supplier')}</label>
            <SupplierSearch selectedSupplier={supplier} onSelect={setSupplier} />
            {errors.supplier && <span className="form-error">{errors.supplier}</span>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="supplier-order-number">
              {t('purchases.supplierOrderNumber')}
            </label>
            <input
              id="supplier-order-number"
              type="text"
              name="supplier_order_number"
              value={formData.supplier_order_number}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="invoice-reference">
              {t('purchases.invoiceReference')}
            </label>
            <input
              id="invoice-reference"
              type="text"
              name="invoice_reference"
              value={formData.invoice_reference}
              onChange={handleChange}
              className="form-input"
              placeholder={t('purchases.invoiceReferencePlaceholder')}
            />
            {errors.invoice_reference && (
              <span className="form-error">{errors.invoice_reference}</span>
            )}
          </div>
        </div>

        <div className="grid-3">
          <div className="form-group">
            <label className="form-label required" htmlFor="operation-type">
              {t('purchases.operationType')}
            </label>
            <select
              id="operation-type"
              name="operation_type"
              value={formData.operation_type}
              onChange={handleChange}
              className="form-select"
            >
              <option value="">{t('filters.selectValue')}</option>
              {operationTypes.map((option) => (
                <option key={option.id} value={option.id}>{option.name}</option>
              ))}
            </select>
            {errors.operation_type && <span className="form-error">{errors.operation_type}</span>}
          </div>

          <div className="form-group">
            <label className="form-label required" htmlFor="purchase-form-category">
              {t('purchases.category')}
            </label>
            <select
              id="purchase-form-category"
              name="category"
              value={formData.category}
              onChange={handleCategoryChange}
              className="form-select"
            >
              <option value="">{t('filters.selectValue')}</option>
              {categories.map((option) => (
                <option key={option.id} value={option.id}>{option.name}</option>
              ))}
            </select>
            {errors.category && <span className="form-error">{errors.category}</span>}
          </div>

          <div className="form-group">
            <label className="form-label required" htmlFor="purchase-form-subcategory">
              {t('purchases.subcategory')}
            </label>
            <select
              id="purchase-form-subcategory"
              name="subcategory"
              value={formData.subcategory}
              onChange={handleChange}
              disabled={!formData.category}
              className="form-select"
            >
              <option value="">
                {formData.category ? t('filters.selectValue') : t('purchases.selectCategoryFirst')}
              </option>
              {subcategories.map((option) => (
                <option key={option.id} value={option.id}>{option.name}</option>
              ))}
            </select>
            {errors.subcategory && <span className="form-error">{errors.subcategory}</span>}
          </div>
        </div>

        <div className="grid-3">
          <div className="form-group">
            <label className="form-label required" htmlFor="effective-date">
              {t('purchases.effectiveDate')}
            </label>
            <input
              id="effective-date"
              type="date"
              name="effective_date"
              value={formData.effective_date}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label required" htmlFor="payment-method">
              {t('purchases.paymentMethod')}
            </label>
            <select
              id="payment-method"
              name="payment_method"
              value={formData.payment_method}
              onChange={handleChange}
              className="form-select"
            >
              <option value="">{t('filters.selectValue')}</option>
              {paymentMethods.map((option) => (
                <option key={option.id} value={option.id}>{option.name}</option>
              ))}
            </select>
            {errors.payment_method && <span className="form-error">{errors.payment_method}</span>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="frequency">
              {t('purchases.frequency')}
            </label>
            <select
              id="frequency"
              name="frequency"
              value={formData.frequency}
              onChange={handleChange}
              className="form-select"
            >
              {FREQUENCIES.map((option) => (
                <option key={option} value={option}>
                  {t(`purchases.frequencyOptions.${option}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex-center gap-20">
          {/* In create mode there is no button: the order does not exist
              yet, so there is nothing to attach a movement to. */}
          {isEditMode && (
            isPaid ? (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleMarkUnpaid}
              >
                {t('purchases.markUnpaid')}
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-accent"
                onClick={() => setPaymentModalOpen(true)}
              >
                {t('purchases.registerPayment')}
              </button>
            )
          )}
          <label className="form-label">
            <input
              type="checkbox"
              name="is_invoiced"
              checked={formData.is_invoiced}
              onChange={handleCheckboxChange}
            />
            {' '}{t('purchases.isInvoiced')}
          </label>
          <label className="form-label">
            <input
              type="checkbox"
              name="is_credit"
              checked={formData.is_credit}
              onChange={handleCheckboxChange}
            />
            {' '}{t('purchases.isCredit')}
          </label>
        </div>
      </fieldset>

      <fieldset className="form-fieldset">
        <legend>{t('purchases.itemsSection')}</legend>

        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>{t('table.itemNumber')}</th>
                <th>{t('table.product')}</th>
                <th>{t('table.description')}</th>
                <th>{t('table.measure')}</th>
                <th>{t('table.quantity')}</th>
                <th>{t('table.unitPrice')}</th>
                <th>{t('purchases.isInventoried')}</th>
                <th>{t('purchases.lineTotal')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <PurchaseOrderItemRow
                  key={index}
                  item={item}
                  index={index}
                  products={products}
                  measures={measures}
                  onItemChange={handleItemChange}
                  onItemRemove={handleItemRemove}
                />
              ))}
            </tbody>
          </table>
        </div>

        {errors.items && <span className="form-error">{errors.items}</span>}

        <button
          type="button"
          onClick={() => setItems((prev) => [...prev, emptyItem()])}
          className="btn btn-info btn-small mt-10"
        >
          {t('purchases.addLine')}
        </button>
      </fieldset>

      <fieldset className="form-fieldset">
        <legend>{t('purchases.totalsSection')}</legend>

        {/* Stacked and right-aligned so the figures line up in one column
            and read like an invoice summary. */}
        <div className="totals-panel">
          <div className="totals-row">
            <span className="totals-label">{t('purchases.subtotal')}</span>
            <span className="totals-value" data-testid="purchase-subtotal">
              {formatCurrency(subtotal)}
            </span>
          </div>

          <div className="totals-row">
            <label className="totals-label" htmlFor="purchase-taxes">
              {t('purchases.taxes')}
            </label>
            <input
              id="purchase-taxes"
              type="text"
              inputMode="decimal"
              name="taxes"
              value={formData.taxes}
              onChange={handleNumericChange}
              className="form-input totals-input"
            />
          </div>

          <div className="totals-row">
            <label className="totals-label" htmlFor="purchase-taxes2">
              {t('purchases.taxes2')}
            </label>
            <input
              id="purchase-taxes2"
              type="text"
              inputMode="decimal"
              name="taxes2"
              value={formData.taxes2}
              onChange={handleNumericChange}
              className="form-input totals-input"
            />
          </div>

          <div className="totals-row totals-row-grand">
            <span className="totals-label">{t('purchases.grandTotal')}</span>
            <span className="totals-value total-emphasis" data-testid="purchase-grand-total">
              {formatCurrency(grandTotal)}
            </span>
          </div>
        </div>
      </fieldset>

      <div className="form-actions">
        <button type="submit" disabled={saving} className="btn btn-primary">
          {saving ? t('common.saving') : t('common.save')}
        </button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          {t('common.back')}
        </button>
      </div>
    </form>

    {isEditMode && (
      <RegisterPaymentModal
        isOpen={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        document={{
          id: initialData.id,
          number: initialData.order_number,
          total: grandTotal
        }}
        onSubmit={(payload) => purchasesService.markPaid(initialData.id, payload)}
        onPaid={() => {
          setPaymentModalOpen(false);
          setIsPaid(true);
        }}
      />
    )}
    </>
  );
};

export default PurchaseOrderForm;
